import React, { createContext, useContext, useState, useCallback } from "react";
import { EN_ENABLED } from "@/lib/config";

export const translations = {
  es: {
    nav: { story: "Nuestra historia", origin: "Origen", origins: "Orígenes", coffees: "Cafés", why: "Por qué", cafeterias: "Hostelería", shop: "Comprar café", horeca: "Hostelería", contact: "Contacto", academia: "Formación" },
    hero: {
      eyebrow: "Café de especialidad",
      title: "Café de especialidad fuera de lo común.",
      text: "Seleccionamos cafés de especialidad con origen y trazabilidad, para disfrutarlos en casa o servirlos en tu negocio.",
      support: "Nacido de nuestra experiencia diaria detrás de la barra.",
      cta1: "Comprar café",
      cta2: "Café para hostelería",
      story: "Conocer nuestra historia",
      badge1: "Finca familiar · Guaraque, Mérida",
      badge2: "Cultivado entre 1.700 y 2.000 m",
      altitudeLabel: "Altitud",
      bagAlt: "Café Naguarà Venezuela, bolsa de 250 g",
    },
    ritual: {
      tag: "Experiencia",
      title: "Nacido detrás de la barra",
      text: "Antes que una marca de café, somos personas que llevan años sirviendo café cada día. Naguarà nace de esa experiencia y de la búsqueda constante del mejor café, con origen y trazabilidad, para casa y para hostelería.",
      items: [
        { t: "Años detrás de la barra", d: "Experiencia real sirviendo café cada día." },
        { t: "Origen y trazabilidad", d: "Cafés seleccionados por su origen y calidad." },
        { t: "Selección de especialidad", d: "Un perfil cuidado en cada café que elegimos." },
        { t: "Casa y hostelería", d: "Para tu casa y para tu negocio." },
      ],
    },
    commerce: {
      shop: { tag: "Compra online", title: "Llévate Naguarà a casa", text: "Cafés de especialidad recién tostados, en tu puerta.", cta: "Ver cafés" },
      sub: { tag: "Suscripción", title: "Café fresco cada mes", text: "Recibe nuestra selección sin pensar en reponer. Cancela cuando quieras.", cta: "Próximamente" },
      horeca: { tag: "Hostelería", title: "Naguarà para tu negocio", text: "Café, formación y asesoramiento para cafeterías, restaurantes y hoteles.", cta: "Hablemos" },
    },
    academia: {
      tag: "Formación · Valencia",
      highlight: "Más que café: formación, consultoría y experiencias alrededor del café de especialidad.",
      title: "Academia Naguarà",
      intro: "Cursos de barista, talleres de latte art, catas y consultoría para cafeterías en Valencia. Compartimos todo lo que hemos aprendido sirviendo café cada día.",
      courses: [
        { t: "Talleres de latte art", d: "Niveles básico, intermedio y avanzado para dominar el vertido y los diseños." },
        { t: "Cursos de barista", d: "Fundamentos, extracción, espresso y leche. De cero a profesional." },
        { t: "Formación para equipos", d: "Programas a medida para cafeterías y restaurantes." },
        { t: "Catas de café de especialidad", d: "Aprende a identificar orígenes, perfiles y matices." },
        { t: "Grupos y empresas", d: "Experiencias de café personalizadas para equipos y eventos." },
        { t: "Consultoría para cafeterías", d: "Selección de café, recetas, calibración de molinos y mejora de procesos." },
      ],
      cta1: "Solicitar información",
      cta2: "Reservar plaza",
      cta3: "Pedir presupuesto para grupos",
      toast: "¡Gracias! Te contactaremos con toda la información.",
      seo: "Curso de barista en Valencia · Taller de latte art en Valencia · Formación para cafeterías · Cursos de café de especialidad · Catas de café en Valencia.",
    },
    marquee: ["¡Naguarà!", "¡Qué brutal!", "Esto sí es café"],
    whatis: {
      tag: "01 — ¿Qué es Naguarà?",
      title: "Una palabra que lo dice todo",
      body: "“Naguarà” es una expresión venezolana que transmite sorpresa, admiración y emoción. Es el ¡wow! que no se puede contener. La reacción que buscamos provocar en cada taza.",
      phrases: ["¡Naguarà!", "¡Qué brutal!", "Esto sí es café."],
    },
    meaning: {
      tag: "El símbolo",
      title: "¿Qué significan los dos puntos?",
      text: "Los dos puntos que acompañan nuestro logotipo representan la sorpresa de descubrir algo extraordinario. Son los ojos de quien prueba un gran café por primera vez y, al mismo tiempo, dos granos de café que recuerdan nuestro origen y nuestra pasión por este producto.",
      eyes: { title: "Ojos de sorpresa", text: "La mirada de quien prueba un gran café por primera vez. El “¡naguarà!” que no se puede contener." },
      beans: { title: "Dos granos de café", text: "Un guiño a nuestro origen y a la pasión por el producto que cultivamos." },
    },
    story: {
      tag: "02 — Nuestra historia",
      title: "Una historia de encuentros",
      p1: "El café unió a dos personas apasionadas por este mundo. Lo que empezó alrededor de una taza se convirtió en un proyecto de vida.",
      p2: "Con el tiempo descubrimos una conexión inesperada entre nuestras raíces y el lugar donde hoy cultivamos café. Lo que empezó como una coincidencia terminó convirtiéndose en una parte fundamental de nuestro proyecto.",
      p3: "Hoy seguimos ligados a ese origen y a las montañas que forman parte de nuestra historia. Detrás de cada taza hay personas reales y una conexión genuina con el lugar de donde viene el café.",
      quote: "NAGUARÀ no es simplemente una marca. Es una historia de encuentros, de origen, de raíces, de familia y de amor por el café.",
      values: ["Encuentros", "Origen", "Raíces", "Familia", "Café"],
    },
    origin: {
      title: "El origen lo cambia todo",
      subtitle: "1.700 – 2.000 metros de altitud",
      text: "Montaña, clima y recolección manual. Procesos cuidados, grano a grano, para que cada taza cuente de dónde viene.",
    },
    products: {
      tag: "04 — Nuestros cafés",
      title: "Elige tu sorpresa",
      add: "Añadir al carrito",
      added: "añadido al carrito",
    },
    encuentra: {
      tag: "Nuestros cafés",
      title: "Encuentra tu café",
      intro: "Cafés de especialidad seleccionados por origen y perfil, recién tostados para disfrutarlos en casa o en tu negocio.",
      add: "Añadir al carrito",
      added: "añadido al carrito",
      view: "Ver ficha de Naguarà",
      specs: {
        altitud: "Altitud",
        clasificacion: "Clasificación",
        variedad: "Variedad",
        proceso: "Proceso",
        cosecha: "Cosecha",
        tueste: "Tueste",
        lote: "Lote",
      },
    },
    choice: {
      tag: "¿Cómo lo quieres?",
      title: "Elige tu camino",
      home: { title: "Para casa", text: "Compra online cafés de especialidad recién tostados, en tu puerta.", cta: "Comprar café" },
      horeca: { title: "Para hostelería", text: "Café, formación y asesoramiento para cafeterías, restaurantes y hoteles.", cta: "Café para hostelería" },
    },
    originWorld: {
      tag: "03 — Orígenes",
      title: "Cafés seleccionados del mundo",
      intro: "Trabajamos con cafés excepcionales de cualquier origen que cumpla nuestros estándares. Nuestro compromiso es la calidad, no las fronteras.",
      more: "Exploramos continuamente nuevos orígenes para encontrar cafés extraordinarios. Cada cosecha es una nueva oportunidad de sorprenderte.",
      countries: [
        { flag: "🇻🇪", name: "Venezuela", role: "Finca familiar · Proyecto propio", text: "Nuestra finca familiar en Guaraque, Mérida, entre 1.700 y 2.000 m. Un proyecto familiar con vocación internacional.", primary: true, badge: "Finca familiar" },
        { flag: "🇧🇷", name: "Brasil", role: "Origen seleccionado", text: "Notas de chocolate, frutos secos y dulzor natural. Ideal para espresso." },
        { flag: "🇨🇴", name: "Colombia", role: "Origen seleccionado", text: "Aromático, complejo y equilibrado. La gran tradición cafetera." },
        { flag: "🇭🇳", name: "Honduras", role: "Origen seleccionado", text: "Cultivado en altura por pequeños productores. Perfil dulce y cítrico con notas de panela y cacao." },
      ],
    },
    why: {
      tag: "05 — ¿Por qué Naguarà?",
      title: "Razones para enamorarte",
      items: [
        { t: "Origen auténtico", d: "Café de origen único de las montañas de Mérida." },
        { t: "Calidad consistente", d: "Cada lote, a la altura de la sorpresa que prometemos." },
        { t: "Espíritu innovador", d: "Lo tradicional, reinterpretado para hoy." },
        { t: "Producción responsable", d: "Respeto por la tierra y por quien la trabaja." },
        { t: "Cultura venezolana", d: "Llevamos el alma de Venezuela en cada taza." },
      ],
    },
    experience: {
      tag: "06 — Experiencia Naguarà",
      title: "Más que café",
      text: "Ritual, comunidad, cultura, diseño, origen y descubrimiento. Naguarà es una experiencia que empieza mucho antes del primer sorbo.",
      tags: ["Ritual", "Comunidad", "Cultura", "Diseño", "Origen", "Descubrimiento"],
    },
    b2b: {
      tag: "Hostelería",
      title: "¿Quieres servir Naguarà?",
      text: "Café para hostelería, formación, asesoramiento, equipamiento y desarrollo de carta. Trabajemos juntos.",
      name: "Nombre",
      business: "Nombre del negocio",
      email: "Email",
      message: "Cuéntanos sobre tu proyecto",
      submit: "Enviar solicitud",
      success: "¡Gracias! Te contactaremos muy pronto.",
    },
    community: {
      tag: "08 — Comunidad",
      title: "Únete a la cultura",
      text: "Síguenos y descubre la vida de marca.",
    },
    footer: {
      slogan: "Café fuera de lo común",
      newsletter: "Recibe historias, lanzamientos y rituales de café.",
      placeholder: "tucorreo@ejemplo.com",
      emailLabel: "Correo electrónico",
      emailError: "Introduce un correo electrónico válido.",
      explore: "Explorar",
      contactTitle: "Contacto",
      location: "Paterna · Valencia · España",
      igAria: "Instagram de Naguarà",
      mailAria: "Correo de Naguarà",
      subscribe: "Suscribirme",
      subscribed: "¡Suscrito! Bienvenido a la cultura Naguarà.",
      rights: "Todos los derechos reservados.",
    },
    avail: {
      badge: "Temporalmente no disponible",
      button: "No disponible",
      notice: "Temporalmente no disponible. Publicaremos los datos cuando estén confirmados.",
      view: "Ver ficha de Naguarà",
      viewSuffix: "— temporalmente no disponible",
      cartBlocked: "No puedes pagar: hay cafés temporalmente no disponibles en el carrito. Quítalos para continuar.",
      cartItem: "No disponible",
      cartRemove: "Quitar los no disponibles",
    },
    seo: {
      siteName: "Naguarà",
      home: {
        title: "Naguarà · Café de especialidad fuera de lo común",
        description: "Cafés de especialidad en grano con origen y trazabilidad. Compra online para casa, café para hostelería y formación de barista en Valencia.",
      },
      hosteleria: {
        title: "Café para hostelería · Naguarà",
        description: "Café de especialidad, formación y asesoramiento para cafeterías, restaurantes y hoteles de Valencia. Cuéntanos tu proyecto y trabajamos juntos.",
        h1: "Café de especialidad para tu barra",
      },
      formacion: {
        title: "Formación de café en Valencia · Academia Naguarà",
        description: "Cursos de barista, talleres de latte art, catas de café de especialidad y consultoría para cafeterías en Valencia.",
        h1: "Aprende café con nosotros",
      },
      historia: {
        title: "Nuestra historia · Naguarà",
        description: "Naguarà nace detrás de la barra: una historia de encuentros, origen y familia alrededor del café de especialidad.",
        h1: "De la barra a la finca",
      },
      contacto: {
        title: "Contacto · Naguarà",
        description: "Escríbenos para pedidos, café para hostelería o formación. Estamos en Paterna, Valencia.",
      },
      cafe: { suffix: "· Café de especialidad en grano, 250 g" },
      notFound: {
        title: "Página no encontrada · Naguarà",
        description: "La página que buscas no existe o ha cambiado de dirección.",
      },
      legal: {
        title: "Textos legales (borrador) · Naguarà",
        description: "Borrador interno pendiente de datos definitivos.",
      },
    },
    formNotice: {
      title: "Formulario en preparación",
      text: "Todavía estamos terminando de configurar el envío de correos de la web. Escríbenos directamente y te respondemos igual de rápido.",
      cta: "Escribir a",
      subjectHoreca: "Café para hostelería",
      subjectFormacion: "Información de formación",
      subjectContacto: "Contacto desde la web",
    },
    form: {
      name: "Nombre y apellidos",
      business: "Nombre del negocio (opcional)",
      email: "Correo electrónico",
      phone: "Teléfono (opcional)",
      message: "Cuéntanos qué necesitas",
      consent: "Acepto que Naguarà use mis datos para responder a esta solicitud.",
      submit: "Enviar solicitud",
      sending: "Enviando…",
      sent: "¡Gracias! Hemos recibido tu mensaje y te respondemos en breve.",
      error: "No hemos podido enviar el mensaje. Escríbenos directamente a",
      rate: "Has enviado varios mensajes seguidos. Prueba de nuevo dentro de un rato.",
      altEmail: "O escríbenos directamente a",
      errors: {
        name: "Escribe tu nombre (mínimo 2 letras).",
        email: "Introduce un correo electrónico válido.",
        message: "Cuéntanos algo más (mínimo 10 caracteres).",
        consent: "Necesitamos tu consentimiento para poder responderte.",
      },
    },
    newsletter: {
      consent: "Acepto recibir novedades de Naguarà por correo.",
      sent: "¡Listo! Te avisaremos de cada novedad.",
      already: "Este correo ya estaba suscrito.",
      error: "No hemos podido completar la suscripción. Inténtalo más tarde.",
    },
    restock: {
      title: "Avísame cuando vuelva",
      text: "Déjanos tu correo y te escribimos en cuanto este café esté publicado y a la venta. Un solo aviso, nada más.",
      cta: "Quiero el aviso",
      consent: "Acepto recibir un único aviso por correo cuando este café vuelva.",
      consentError: "Marca la casilla para poder avisarte.",
      sent: "Anotado. Te escribiremos en cuanto este café vuelva.",
      already: "Ya tenías el aviso pedido para este café.",
      error: "No hemos podido guardar tu aviso. Inténtalo más tarde.",
    },
    contacto: {
      tag: "Contacto",
      title: "Hablemos de café",
      intro: "Pedidos, café para hostelería, formación o cualquier duda: escríbenos y te responde una persona del equipo.",
      dataTitle: "Datos de contacto",
      emailTitle: "Correo",
      igTitle: "Instagram",
      locationTitle: "Dónde estamos",
      locationNote: "Atención por correo y cita previa. Envíos a toda la península.",
    },
    leads: {
      hosteleria: {
        eyebrow: "Café para hostelería",
        text: "Empezamos escuchando tu carta, tu volumen y tu equipo. A partir de ahí elegimos juntos el café, ajustamos molienda y receta en tu propia máquina y formamos a quien está en la barra. Trabajamos con cafeterías, restaurantes y hoteles de Valencia y alrededores.",
      },
      formacion: {
        eyebrow: "Formación en Valencia",
        text: "Formación práctica, en grupos pequeños y siempre con la máquina encendida: extracción, molienda, leche, latte art, cata y puesta en marcha de cafeterías. Si no ves el curso que necesitas, cuéntanoslo y lo montamos a medida.",
      },
      historia: {
        eyebrow: "Nuestra historia",
        text: "Naguarà es la palabra que se te escapa cuando algo te sorprende. Nos pasó detrás de la barra, sirviendo café cada día, y terminó convirtiéndose en un proyecto con origen, finca familiar y una forma muy concreta de entender el café de especialidad.",
      },
    },
    qr: {
      title: "Naguarà · te llevamos al café",
      text: "Un momento, te estamos llevando al café.",
      cta: "Continuar",
    },
    notFound: {
      title: "Aquí no hay café",
      text: "La página que buscas no existe o ha cambiado de dirección.",
      cta: "Volver a la portada",
      cta2: "Ver nuestros cafés",
    },
    legal: {
      draft: "El texto es definitivo salvo los datos marcados como pendientes, que publicaremos en cuanto estén verificados. Si necesitas cualquier información legal antes, escríbenos a info@gruporitual.com y te la damos por correo.",
      pending: "pendiente de publicar",
      footerTitle: "Legal",
      items: {
        "aviso-legal": "Aviso legal",
        privacidad: "Privacidad",
        cookies: "Cookies",
        "condiciones-de-compra": "Condiciones de compra",
        "envios-y-devoluciones": "Envíos y devoluciones",
      },
    },
    producto: {
      viewLabel: "Ver ficha y comprar café",
      packAlt: "Bolsa de café de especialidad",
      back: "Volver a la portada",
      specsTitle: "Ficha técnica",
      notesTitle: "Notas de cata",
      pending: "Temporalmente no disponible. Publicaremos los datos cuando estén confirmados.",
      notFound: "No hemos encontrado este café.",
    },
    cart: {
      title: "Tu carrito",
      empty: "Tu carrito está vacío",
      subtotal: "Subtotal",
      shipping: "Envío",
      shippingIncluded: "Incluido",
      shippingFree: "Gratis",
      freeFrom: "Te falta para el envío gratis:",
      freeMissing: "Te faltan {amount} para conseguir envío gratis",
      freeReached: "¡Ya tienes envío gratis!",
      conditions: "Envío a España peninsular: {flat} · gratis desde {free}.",
      postalLabel: "Código postal de entrega",
      postalPlaceholder: "46001",
      postalHint: "Lo usamos para comprobar la zona de envío. Solo enviamos a España peninsular por ahora.",
      postalInvalid: "Escribe un código postal español de 5 cifras.",
      zoneBlocked:
        "Todavía no hacemos envíos a Baleares, Canarias, Ceuta ni Melilla. Escríbenos a info@gruporitual.com y te preparamos un presupuesto de envío.",
      total: "Total",
      checkout: "Pagar",
      processing: "Procesando…",
      remove: "Quitar",
      close: "Cerrar el carrito",
      more: "Añadir uno más",
      less: "Quitar uno",
      maxQty: "Máximo 10 unidades por café",
      error: "No hemos podido iniciar el pago. Inténtalo de nuevo en un momento.",
      secure: "Pago seguro con tarjeta a través de Stripe.",
    },
  },
  en: {
    nav: { story: "Our story", origin: "Origin", origins: "Origins", coffees: "Coffees", why: "Why", cafeterias: "Hospitality", shop: "Shop coffee", horeca: "Hospitality", contact: "Contact", academia: "Training" },
    hero: {
      eyebrow: "Specialty coffee",
      title: "Specialty coffee out of the ordinary.",
      text: "We select specialty coffees with origin and traceability, to enjoy at home or serve in your business.",
      support: "Born from our daily experience behind the bar.",
      cta1: "Shop coffee",
      cta2: "Coffee for hospitality",
      story: "Discover our story",
      badge1: "Family farm · Guaraque, Mérida",
      badge2: "Grown between 1,700 and 2,000 m",
      altitudeLabel: "Altitude",
      bagAlt: "Naguarà Venezuela coffee, 250 g bag",
    },
    ritual: {
      tag: "Experience",
      title: "Born behind the bar",
      text: "Before being a coffee brand, we are people who have spent years serving coffee every day. Naguarà is born from that experience and the constant search for the best coffee, with origin and traceability, for home and hospitality.",
      items: [
        { t: "Years behind the bar", d: "Real experience serving coffee every day." },
        { t: "Origin & traceability", d: "Coffees selected for their origin and quality." },
        { t: "Specialty selection", d: "A careful profile in every coffee we choose." },
        { t: "Home & hospitality", d: "For your home and your business." },
      ],
    },
    commerce: {
      shop: { tag: "Online shop", title: "Take Naguarà home", text: "Freshly roasted specialty coffee, at your door.", cta: "See coffees" },
      sub: { tag: "Subscription", title: "Fresh coffee every month", text: "Get our selection without thinking about restocking. Cancel anytime.", cta: "Coming soon" },
      horeca: { tag: "Wholesale", title: "Naguarà for your business", text: "Coffee, training and consulting for coffee shops, restaurants and hotels.", cta: "Let's talk" },
    },
    academia: {
      tag: "Training · Valencia",
      highlight: "More than coffee: training, consulting and experiences around specialty coffee.",
      title: "Naguarà Academy",
      intro: "Barista courses, latte art workshops, cuppings and consulting for coffee shops in Valencia. We share everything we've learned serving coffee every day.",
      courses: [
        { t: "latte art workshops", d: "Beginner, intermediate and advanced levels to master pouring and designs." },
        { t: "Barista courses", d: "Fundamentals, extraction, espresso and milk. From zero to professional." },
        { t: "Team training", d: "Tailored programs for coffee shops and restaurants." },
        { t: "Specialty coffee cuppings", d: "Learn to identify origins, profiles and nuances." },
        { t: "Groups & companies", d: "Personalized coffee experiences for teams and events." },
        { t: "Coffee shop consulting", d: "Coffee selection, recipes, grinder calibration and process improvement." },
      ],
      cta1: "Request information",
      cta2: "Book a spot",
      cta3: "Get a group quote",
      toast: "Thanks! We'll get in touch with all the information.",
      seo: "Barista course in Valencia · latte art workshop in Valencia · Coffee shop training · Specialty coffee courses · Coffee cuppings in Valencia.",
    },
    marquee: ["¡Naguarà!", "So good!", "This is real coffee"],
    whatis: {
      tag: "01 — What is Naguarà?",
      title: "One word says it all",
      body: "“Naguarà” is a Venezuelan expression of surprise, admiration and emotion. It's the unstoppable wow — the reaction we want to spark in every cup.",
      phrases: ["¡Naguarà!", "¡Qué brutal!", "This is real coffee."],
    },
    meaning: {
      tag: "The symbol",
      title: "What do the two dots mean?",
      text: "The two dots beside our logo represent the surprise of discovering something extraordinary. They are the eyes of someone tasting a great coffee for the first time and, at the same time, two coffee beans that recall our origin and our passion for this product.",
      eyes: { title: "Eyes of surprise", text: "The look of someone tasting a great coffee for the first time. The unstoppable “naguarà!”." },
      beans: { title: "Two coffee beans", text: "A nod to our origin and our passion for the product we grow." },
    },
    story: {
      tag: "02 — Our story",
      title: "A story of encounters",
      p1: "Coffee brought together two people passionate about this world. What began around a cup became a life project.",
      p2: "Over time we discovered an unexpected connection between our roots and the place where we grow coffee today. What started as a coincidence became a fundamental part of our project.",
      p3: "Today we remain connected to that origin and to the mountains that are part of our story. Behind every cup there are real people and a genuine connection with the place the coffee comes from.",
      quote: "NAGUARÀ is not just a brand. It's a story of encounters, of origin, of roots, of family and of love for coffee.",
      values: ["Encounters", "Origin", "Roots", "Family", "Coffee"],
    },
    origin: {
      title: "Origin changes everything",
      subtitle: "1,700 – 2,000 metres of altitude",
      text: "Mountains, climate and hand-picking. Careful processing, bean by bean, so every cup tells where it comes from.",
    },
    products: {
      tag: "04 — Our coffees",
      title: "Choose your surprise",
      add: "Add to cart",
      added: "added to cart",
    },
    encuentra: {
      tag: "Our coffees",
      title: "Find your coffee",
      intro: "Specialty coffees selected by origin and profile, freshly roasted to enjoy at home or in your business.",
      add: "Add to cart",
      added: "added to cart",
      view: "View Naguarà",
      specs: {
        altitud: "Altitude",
        clasificacion: "Classification",
        variedad: "Variety",
        proceso: "Process",
        cosecha: "Harvest",
        tueste: "Roast",
        lote: "Lot",
      },
    },
    choice: {
      tag: "How do you want it?",
      title: "Choose your path",
      home: { title: "For home", text: "Buy freshly roasted specialty coffee online, at your door.", cta: "Shop coffee" },
      horeca: { title: "For hospitality", text: "Coffee, training and consulting for coffee shops, restaurants and hotels.", cta: "Coffee for hospitality" },
    },
    originWorld: {
      tag: "03 — Origins",
      title: "Coffees selected from around the world",
      intro: "We work with exceptional coffees from any origin that meets our standards. Our commitment is to quality, not to borders.",
      more: "We continuously explore new origins to find extraordinary coffees. Every harvest is a new chance to surprise you.",
      countries: [
        { flag: "🇻🇪", name: "Venezuela", role: "Our own farm · Family project", text: "Our own farm in Guaraque, Mérida, between 1,700 and 2,000 m. A family project with an international vision.", primary: true, badge: "Our own farm" },
        { flag: "🇧🇷", name: "Brasil", role: "An origin we explore", text: "Chocolate, nutty notes and natural sweetness. Perfect for espresso." },
        { flag: "🇨🇴", name: "Colombia", role: "An origin we explore", text: "Aromatic, complex and balanced. The great coffee tradition." },
        { flag: "🇭🇳", name: "Honduras", role: "An origin we explore", text: "High-grown by small producers. Sweet, citric profile with brown sugar and cocoa notes." },
      ],
    },
    why: {
      tag: "05 — Why Naguarà?",
      title: "Reasons to fall in love",
      items: [
        { t: "Authentic origin", d: "Single-origin coffee from the mountains of Mérida." },
        { t: "Consistent quality", d: "Every batch lives up to the surprise we promise." },
        { t: "Innovative spirit", d: "Tradition, reinterpreted for today." },
        { t: "Responsible farming", d: "Respect for the land and those who work it." },
        { t: "Venezuelan culture", d: "We carry the soul of Venezuela in every cup." },
      ],
    },
    experience: {
      tag: "06 — Naguarà experience",
      title: "More than coffee",
      text: "Ritual, community, culture, design, origin and discovery. Naguarà is an experience that begins long before the first sip.",
      tags: ["Ritual", "Community", "Culture", "Design", "Origin", "Discovery"],
    },
    b2b: {
      tag: "Hospitality",
      title: "Want to serve Naguarà?",
      text: "Coffee for hospitality, training, consulting, equipment and menu development. Let's work together.",
      name: "Name",
      business: "Business name",
      email: "Email",
      message: "Tell us about your project",
      submit: "Send request",
      success: "Thanks! We'll get in touch very soon.",
    },
    community: {
      tag: "08 — Community",
      title: "Join the culture",
      text: "Follow us and discover the life of the brand.",
    },
    footer: {
      slogan: "Coffee out of the ordinary",
      newsletter: "Get stories, launches and coffee rituals.",
      placeholder: "you@example.com",
      emailLabel: "Email address",
      emailError: "Please enter a valid email address.",
      explore: "Explore",
      contactTitle: "Contact",
      location: "Paterna · Valencia · Spain",
      igAria: "Instagram de Naguarà",
      mailAria: "Naguarà email",
      subscribe: "Subscribe",
      subscribed: "Subscribed! Welcome to the Naguarà culture.",
      rights: "All rights reserved.",
    },
    avail: {
      badge: "Temporarily unavailable",
      button: "Unavailable",
      notice: "Temporarily unavailable. We will publish the data once it is confirmed.",
      view: "View the",
      viewSuffix: "— unavailable",
      cartBlocked: "You cannot check out: there are temporarily unavailable coffees in your cart. Remove them to continue.",
      cartItem: "Unavailable",
      cartRemove: "Remove unavailable items",
    },
    seo: {
      siteName: "Naguarà",
      home: {
        title: "Naguarà · Specialty coffee out of the ordinary",
        description: "Whole-bean specialty coffee with origin and traceability. Buy online for home, coffee for hospitality and barista training in Valencia.",
      },
      hosteleria: {
        title: "Coffee for hospitality · Naguarà",
        description: "Specialty coffee, training and consulting for coffee shops, restaurants and hotels in Valencia. Tell us about your project.",
        h1: "Specialty coffee for your bar",
      },
      formacion: {
        title: "Coffee training in Valencia · Naguarà Academy",
        description: "Barista courses, latte art workshops, specialty coffee cuppings and consulting for coffee shops in Valencia.",
        h1: "Learn coffee with us",
      },
      historia: {
        title: "Our story · Naguarà",
        description: "Naguarà was born behind the bar: a story of encounters, origin and family around specialty coffee.",
        h1: "From the bar to the farm",
      },
      contacto: {
        title: "Contact · Naguarà",
        description: "Write to us about orders, hospitality coffee or training. We are based in Paterna, Valencia.",
      },
      cafe: { suffix: "· Whole-bean specialty coffee, 250 g" },
      notFound: {
        title: "Page not found · Naguarà",
        description: "The page you are looking for does not exist or has moved.",
      },
      legal: {
        title: "Legal texts (draft) · Naguarà",
        description: "Internal draft pending final company data.",
      },
    },
    formNotice: {
      title: "Form coming soon",
      text: "We are still setting up the website email delivery. Write to us directly and we will answer just as fast.",
      cta: "Write to",
      subjectHoreca: "Coffee for hospitality",
      subjectFormacion: "Training information",
      subjectContacto: "Contact from the website",
    },
    form: {
      name: "Full name",
      business: "Business name (optional)",
      email: "Email address",
      phone: "Phone (optional)",
      message: "Tell us what you need",
      consent: "I agree that Naguarà may use my data to answer this enquiry.",
      submit: "Send request",
      sending: "Sending…",
      sent: "Thank you! We have received your message and will reply shortly.",
      error: "We could not send the message. Please write directly to",
      rate: "You have sent several messages in a row. Please try again later.",
      altEmail: "Or write to us directly at",
      errors: {
        name: "Please enter your name (at least 2 letters).",
        email: "Please enter a valid email address.",
        message: "Tell us a bit more (at least 10 characters).",
        consent: "We need your consent in order to reply.",
      },
    },
    newsletter: {
      consent: "I agree to receive Naguarà news by email.",
      sent: "Done! We will let you know about every release.",
      already: "This email was already subscribed.",
      error: "We could not complete the subscription. Please try again later.",
    },
    restock: {
      title: "Tell me when it is back",
      text: "Leave your email and we will write as soon as this coffee is published and on sale. One single alert, nothing else.",
      cta: "Notify me",
      consent: "I agree to receive one single email when this coffee is back.",
      consentError: "Please tick the box so we can notify you.",
      sent: "Noted. We will write as soon as this coffee is back.",
      already: "You had already requested the alert for this coffee.",
      error: "We could not save your alert. Please try again later.",
    },
    contacto: {
      tag: "Contact",
      title: "Let's talk coffee",
      intro: "Orders, hospitality coffee, training or any question: write to us and a real person from the team will answer.",
      dataTitle: "Contact details",
      emailTitle: "Email",
      igTitle: "Instagram",
      locationTitle: "Where we are",
      locationNote: "Email support and appointments. We ship across mainland Spain.",
    },
    leads: {
      hosteleria: {
        eyebrow: "Coffee for hospitality",
        text: "We start by listening to your menu, your volume and your team. From there we choose the coffee together, dial in grind and recipe on your own machine and train whoever stands behind the bar. We work with coffee shops, restaurants and hotels in and around Valencia.",
      },
      formacion: {
        eyebrow: "Training in Valencia",
        text: "Hands-on training in small groups, always with the machine running: extraction, grinding, milk, latte art, cupping and coffee shop set-up. If you cannot see the course you need, tell us and we will build it for you.",
      },
      historia: {
        eyebrow: "Our story",
        text: "Naguarà is the word that slips out when something surprises you. It happened to us behind the bar, serving coffee every day, and it grew into a project with origin, a family farm and a very specific way of understanding specialty coffee.",
      },
    },
    qr: {
      title: "Naguarà · taking you to the coffee",
      text: "One moment, we are taking you to the coffee.",
      cta: "Continue",
    },
    notFound: {
      title: "No coffee here",
      text: "The page you are looking for does not exist or has moved.",
      cta: "Back to home",
      cta2: "See our coffees",
    },
    legal: {
      draft: "The text is final except for the details marked as pending, which we will publish as soon as they are verified. If you need any legal information before that, write to info@gruporitual.com and we will send it by email.",
      pending: "pending publication",
      footerTitle: "Legal",
      items: {
        "aviso-legal": "Legal notice",
        privacidad: "Privacy",
        cookies: "Cookies",
        "condiciones-de-compra": "Terms of purchase",
        "envios-y-devoluciones": "Shipping and returns",
      },
    },
    producto: {
      viewLabel: "Ver ficha y comprar café",
      packAlt: "Specialty coffee bag",
      back: "Back to home",
      specsTitle: "Technical sheet",
      notesTitle: "Tasting notes",
      pending: "Temporarily unavailable. We will publish the data once it is confirmed.",
      notFound: "We could not find this coffee.",
    },
    cart: {
      title: "Your cart",
      empty: "Your cart is empty",
      subtotal: "Subtotal",
      shipping: "Shipping",
      shippingIncluded: "Included",
      shippingFree: "Free",
      freeFrom: "Left for free shipping:",
      freeMissing: "{amount} away from free shipping",
      freeReached: "You've got free shipping!",
      conditions: "Shipping to mainland Spain: {flat} · free from {free}.",
      postalLabel: "Delivery postcode",
      postalPlaceholder: "46001",
      postalHint: "We use it to check the delivery zone. We only ship to mainland Spain for now.",
      postalInvalid: "Enter a 5-digit Spanish postcode.",
      zoneBlocked:
        "We do not ship to the Balearic Islands, Canary Islands, Ceuta or Melilla yet. Write to info@gruporitual.com and we will quote the shipping for you.",
      total: "Total",
      checkout: "Checkout",
      processing: "Processing…",
      remove: "Remove",
      close: "Close the cart",
      more: "Add one more",
      less: "Remove one",
      maxQty: "Maximum 10 units per coffee",
      error: "We could not start the payment. Please try again in a moment.",
      secure: "Secure card payment through Stripe.",
    },
  },
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  // The EN toggle is hidden before launch: the site stays in Spanish even if a
  // previous visit stored "en". Nothing else in the i18n layer changes.
  const [lang, setLang] = useState(() =>
    EN_ENABLED ? localStorage.getItem("naguara_lang") || "es" : "es"
  );

  const toggle = useCallback(() => {
    setLang((prev) => {
      const next = prev === "es" ? "en" : "es";
      localStorage.setItem("naguara_lang", next);
      return next;
    });
  }, []);

  const t = translations[lang];
  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
